/**
 * Custom hook for checking user suspension status
 * Modular and reusable across components
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/useAuthStore';

export interface SuspensionStatus {
  isSuspended: boolean;
  downloadEnabled: boolean;
  daysRemaining: number | null;
  downloadExpiresAt: string | null;
  suspensionStartedAt: string | null;
  isExpired: boolean;
  autoDelete: boolean; // If true, account will be deleted after grace period
}

export function useSuspensionStatus() {
  const userId = useAuthStore((s) => s.user?.id);
  const [status, setStatus] = useState<SuspensionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkSuspensionStatus = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('is_suspended, suspension_download_enabled, suspension_download_expires_at, suspension_started_at, suspension_grace_period_days, suspension_auto_delete')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error checking suspension status:', error);
        setIsLoading(false);
        return;
      }

      if (!profile || !profile.is_suspended) {
        setStatus({
          isSuspended: false,
          downloadEnabled: false,
          daysRemaining: null,
          downloadExpiresAt: null,
          suspensionStartedAt: null,
          isExpired: false,
          autoDelete: false,
        });
        setIsLoading(false);
        return;
      }

      const downloadEnabled = profile.suspension_download_enabled === true;
      
      let daysRemaining: number | null = null;
      if (downloadEnabled) {
        if (profile.suspension_download_expires_at) {
          const expiresAt = new Date(profile.suspension_download_expires_at);
          const now = new Date();
          if (expiresAt > now) {
            const diffMs = expiresAt.getTime() - now.getTime();
            daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
          } else {
            daysRemaining = 0;
          }
        } else if (profile.suspension_started_at) {
          const startedAt = new Date(profile.suspension_started_at);
          const gracePeriodDays = profile.suspension_grace_period_days || 14;
          const daysElapsed = Math.floor((Date.now() - startedAt.getTime()) / (1000 * 60 * 60 * 24));
          daysRemaining = Math.max(0, gracePeriodDays - daysElapsed);
        }
      }

      const isExpired = daysRemaining !== null && daysRemaining === 0;
      const autoDelete = profile.suspension_auto_delete === true;

      setStatus({
        isSuspended: true,
        downloadEnabled,
        daysRemaining,
        downloadExpiresAt: profile.suspension_download_expires_at,
        suspensionStartedAt: profile.suspension_started_at,
        isExpired,
        autoDelete,
      });
    } catch (error) {
      console.error('Error in suspension check:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    checkSuspensionStatus();

    if (!userId) return;

    // Subscribe to profile changes
    const channel = supabase
      .channel(`suspension-status-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`,
        },
        () => {
          checkSuspensionStatus();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, checkSuspensionStatus]);

  return {
    status,
    isLoading,
    refresh: checkSuspensionStatus,
  };
}

